import { LoadingButton as Button } from '@mui/lab';
import FormHelperText from '@mui/material/FormHelperText';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';

import 'react-phone-number-input/style.css';
import { Input } from '../../components/Input';
import { Label } from '../../components/Label';
import { getErrorContent } from '../../utils/errors';
import { signInUser } from '../Login/async';

const useStyles = makeStyles(theme => ({
    modalContent: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        padding: theme.spacing(2, 4, 3),
        boxShadow: theme.shadows[5],
        backgroundColor: 'black',
    },
    '@global': {
        '.PhoneInputCountry': {
            marginRight: 8,
            verticalAlign: 'middle',
            marginTop: -8,
            fontSize: 18,
        },
    },

    title: {
        color: '#FFF',
        lineHeight: '1.625!important',
        fontSize: '1.8rem',
        marginBottom: 0,
        marginTop: 0,
        fontWeight: 600,
        paddingBottom: '2rem',
    },
    errorText: {
        fontSize: '1.45rem',
        fontWeight: 'normal',
        marginBottom: theme.spacing(),
        textAlign: 'center',
    },
    controls: {
        marginBottom: theme.spacing(),
        '& input': {
            fontSize: 14,
        },
    },
    formActions: {
        marginTop: theme.spacing(3),
        background: 'none',
    },
    btnWrapper: {
        position: 'relative',
    },
    updateButton: {
        padding: '1.6rem',
        borderRadius: '.8rem',
        textTransform: 'uppercase',
        background: '#ff6c2f',
        fontSize: 16,
        color: '#FFF',
        fontWeight: 600,
        '&:hover': {
            background: '#ff6c2f',
        },
    },
    btnDisabled: {
        backgroundColor: '#cccccc!important',
        color: '#666666!important',
    },
    error: {
        fontSize: '1.45rem',
        textAlign: 'center',
    },

    label: {
        fontSize: '1.45rem',
        color: 'white',
    },
}));

const defaultState = {
    email: '',
    password: '',
    customerName: '',
};

export const UpdateProfile = ({ open, onClose, hasActiveSubscription }) => {
    const classes = useStyles();

    const [submitMessage, setSubmitMessage] = useState(null);

    const updateUser = async credentials => {
        const payload = {
            email: credentials.email,
            password: credentials.password,
            fullName: credentials?.customerName,
        };

        await fetch('/user/v1/update', {
            method: 'PATCH',
            headers: {
                'Content-type': 'application/json',
                'Accept-Encoding': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(payload),
        })
            .then(async res => {
                if (!res.ok) {
                    throw new Error('Failed to update user data');
                }

                const data = await res.json();
                if (!data.success) {
                    setSubmitMessage('Failed to update user data');
                    return;
                }
                if (hasActiveSubscription) {
                    window.location.href = 'https://play.wi-flix.com';
                } else {
                    window.location.href = 'https://checkout.wi-flix.com';
                }
            })
            .catch(err => {
                if (Object.hasOwnProperty.call(err, 'errors')) {
                    setSubmitMessage(getErrorContent(err.errors));
                    return;
                }

                setSubmitMessage(getErrorContent(err));
            });
    };

    const formik = useFormik({
        initialValues: defaultState,
        validateOnChange: false,
        validationSchema: yup.object().shape({
            email: yup.string().email().required('Email is required'),
            password: yup.string().required(),
            customerName: yup.string().optional(),
        }),
        onSubmit: updateUser,
    });

    const { values, errors, handleChange } = formik;
    const onChange = (name, value) => handleChange({ target: { name, value } });

    return (
        <Modal
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }

                onClose();
            }}
            aria-labelledby="checkout-modal-title"
            aria-describedby="checkout-modal-description"
        >
            <div className={classes.modalContent}>
                <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                    <Typography className={classes.title} variant="h3">
                        Update your account details
                    </Typography>

                    <div className={classes.controls}>
                        <Label classes={{ label: classes.label }} value="Your Email" />
                        <Input
                            variant="outlined"
                            name="email"
                            value={values.email}
                            placeholder="Enter your email"
                            type="email"
                            autoComplete="email"
                            fullWidth
                            error={errors.email}
                            onChange={onChange}
                        />
                    </div>

                    <div className={classes.controls}>
                        <Label classes={{ label: classes.label }} value="Your Full Name" />

                        <Input
                            classes={{ textFieldRoot: classes.input }}
                            variant="outlined"
                            name="customerName"
                            value={values.customerName}
                            placeholder="Enter your full name"
                            type="text"
                            autoComplete="off"
                            fullWidth
                            error={errors.customerName}
                            onChange={onChange}
                        />
                    </div>

                    <div className={classes.controls}>
                        <Label classes={{ label: classes.label }} value="Your Password" />
                        <Input
                            variant="outlined"
                            name="password"
                            value={values.password}
                            placeholder="Enter password"
                            type="password"
                            autoComplete="new-password"
                            fullWidth
                            error={errors.password}
                            onChange={onChange}
                        />
                    </div>

                    {Boolean(submitMessage) && (
                        <FormHelperText
                            error
                            classes={{
                                root: classes.errorText,
                            }}
                        >
                            {submitMessage}
                        </FormHelperText>
                    )}

                    <fieldset className={classes.formActions}>
                        <div className={classes.btnWrapper}>
                            <Button
                                loading={formik.isSubmitting}
                                disabled={formik.isSubmitting}
                                classes={{
                                    root: classes.updateButton,
                                    disabled: classes.btnDisabled,
                                }}
                                fullWidth
                                variant="contained"
                                type="submit"
                            >
                                Update Profile
                            </Button>
                        </div>
                    </fieldset>
                </form>
            </div>
        </Modal>
    );
};
